import type { Coordinates } from '../../types';

// ===== CROSS-SLICE DEPENDENCIES =====
// Interface for accessing actions from other slices (Zustand best practice)
interface StoreWithCrossDependencies {
  navigation: NavigationSlice['navigation'];
  tasks: {
    selectTask: (id: string | null) => void;
    closeForm: () => void;
    openDetails: () => void;
  };
  map: {
    zoomTo: (coords: Coordinates | null) => void;
  };
}

// ===== SLICE INTERFACE =====

export interface NavigationSlice {
  navigation: {
    showDialog: boolean;
    pending: { taskId: string; coordinates: Coordinates } | null;
    show: (taskId: string, coordinates: Coordinates) => void;
    hide: () => void;
    execute: () => void;
  };
}

// ===== SLICE CREATOR =====
// Phase 2: Full implementation with Immer v10

export const createNavigationSlice = (
  set: (fn: (state: NavigationSlice) => void) => void,
  get: () => NavigationSlice,
  _store: unknown
) => ({
  navigation: {
    showDialog: false,
    pending: null,

    /**
     * Show navigation confirmation dialog
     * Used when user clicks on a task while editing another task
     * @param taskId - ID of the task to navigate to
     * @param coordinates - Coordinates of the task for zooming
     */
    show: (taskId: string, coordinates: Coordinates) => {
      set((state) => {
        state.navigation.showDialog = true;
        state.navigation.pending = { taskId, coordinates };
      });
    },

    /**
     * Hide navigation confirmation dialog
     * Cancels pending navigation
     */
    hide: () => {
      set((state) => {
        state.navigation.showDialog = false;
        state.navigation.pending = null;
      });
    },

    /**
     * Execute pending navigation
     * Closes task form, opens task preview, and zooms to task coordinates.
     *
     * Note: this path is largely orphaned since `useTaskNavigation.executePendingNavigation`
     * now handles URL-driven navigation. Kept here as a lightweight fallback that
     * stays consistent with the artifact slice contract introduced in R1.
     */
    execute: () => {
      const { pending } = get().navigation;
      if (!pending) return;

      // Access cross-slice actions
      const store = get() as StoreWithCrossDependencies;

      // Close task form, open task preview, and navigate to task
      store.tasks.closeForm();
      store.tasks.selectTask(pending.taskId);
      store.tasks.openDetails();
      store.map.zoomTo(pending.coordinates);

      // Hide dialog after execution
      set((state) => {
        state.navigation.showDialog = false;
        state.navigation.pending = null;
      });
    },
  },
});
